import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    VStack,
    HStack,
    Select,
    Badge,
    Spinner,
    Text,
    useDisclosure
} from '@chakra-ui/react';
import { FiPlus, FiEdit, FiTrash2, FiEye } from 'react-icons/fi';
import registrationService from '../services/registrationService';
import niveauService from '../services/niveauService';

const RegistrationManagement = () => {
    const [registrations, setRegistrations] = useState([]);
    const [niveaux, setNiveaux] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRegistration, setSelectedRegistration] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    useEffect(() => {
        fetchData();
    }, [statusFilter]);

    const fetchData = async () => {
        try {
            const [regResponse, nivResponse] = await Promise.all([
                registrationService.getRegistrations(statusFilter ? { status: statusFilter } : {}),
                niveauService.getAllNiveaux()
            ]);
            setRegistrations(regResponse.data);
            setNiveaux(nivResponse.data);
        } catch (error) {
            toast({
                title: 'خطأ في تحميل البيانات',
                status: 'error',
                duration: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, newStatus, notes) => {
        try {
            await registrationService.updateRegistrationStatus(id, { status: newStatus, notes });
            toast({
                title: 'تم تحديث الحالة بنجاح',
                status: 'success',
                duration: 3000
            });
            fetchData();
            onClose();
        } catch (error) {
            toast({
                title: 'خطأ في التحديث',
                status: 'error',
                duration: 3000
            });
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذا الطلب؟')) {
            try {
                await registrationService.deleteRegistration(id);
                toast({
                    title: 'تم الحذف بنجاح',
                    status: 'success',
                    duration: 3000
                });
                fetchData();
            } catch (error) {
                toast({
                    title: 'خطأ في الحذف',
                    status: 'error',
                    duration: 3000
                });
            }
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'yellow',
            contacted: 'blue',
            enrolled: 'green',
            rejected: 'red'
        };
        return colors[status] || 'gray';
    };

    const getStatusText = (status) => {
        const texts = {
            pending: 'قيد الانتظار',
            contacted: 'تم التواصل',
            enrolled: 'مسجل',
            rejected: 'مرفوض'
        };
        return texts[status] || status;
    };

    if (loading) {
        return (
            <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
                <Spinner size="xl" color="blue.500" />
            </Box>
        );
    }

    return (
        <Container maxW="1600px" py={8}>
            <HStack justify="space-between" mb={8}>
                <Heading size="xl" fontFamily="Cairo, sans-serif">
                    طلبات التسجيل ({registrations.length})
                </Heading>
                <Select
                    w="250px"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    fontFamily="Cairo, sans-serif"
                >
                    <option value="">جميع الحالات</option>
                    <option value="pending">قيد الانتظار</option>
                    <option value="contacted">تم التواصل</option>
                    <option value="enrolled">مسجل</option>
                    <option value="rejected">مرفوض</option>
                </Select>
            </HStack>

            <Box bg="white" borderRadius="xl" boxShadow="lg" overflow="hidden">
                <Table variant="simple">
                    <Thead bg="gray.100">
                        <Tr>
                            <Th fontFamily="Cairo, sans-serif">اسم الطالب</Th>
                            <Th fontFamily="Cairo, sans-serif">ولي الأمر</Th>
                            <Th fontFamily="Cairo, sans-serif">رقم الهاتف</Th>
                            <Th fontFamily="Cairo, sans-serif">البريد الإلكتروني</Th>
                            <Th fontFamily="Cairo, sans-serif">المستوى</Th>
                            <Th fontFamily="Cairo, sans-serif">الحالة</Th>
                            <Th fontFamily="Cairo, sans-serif">التاريخ</Th>
                            <Th fontFamily="Cairo, sans-serif">إجراءات</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {registrations.map((reg) => (
                            <Tr key={reg._id} _hover={{ bg: 'gray.50' }}>
                                <Td fontFamily="Cairo, sans-serif">{reg.studentName}</Td>
                                <Td fontFamily="Cairo, sans-serif">{reg.parentName}</Td>
                                <Td fontFamily="Cairo, sans-serif">{reg.parentPhone}</Td>
                                <Td fontFamily="Cairo, sans-serif" fontSize="sm">{reg.email}</Td>
                                <Td>
                                    <Badge colorScheme="purple" fontFamily="Cairo, sans-serif">
                                        {reg.niveau?.name || 'غير محدد'}
                                    </Badge>
                                </Td>
                                <Td>
                                    <Badge colorScheme={getStatusColor(reg.status)} fontFamily="Cairo, sans-serif">
                                        {getStatusText(reg.status)}
                                    </Badge>
                                </Td>
                                <Td fontFamily="Cairo, sans-serif" fontSize="sm">
                                    {new Date(reg.createdAt).toLocaleDateString('ar-EG')}
                                </Td>
                                <Td>
                                    <HStack spacing={2}>
                                        <IconButton
                                            icon={<FiEdit />}
                                            size="sm"
                                            colorScheme="blue"
                                            variant="ghost"
                                            onClick={() => {
                                                setSelectedRegistration(reg);
                                                onOpen();
                                            }}
                                        />
                                        <IconButton
                                            icon={<FiTrash2 />}
                                            size="sm"
                                            colorScheme="red"
                                            variant="ghost"
                                            onClick={() => handleDelete(reg._id)}
                                        />
                                    </HStack>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>

                {registrations.length === 0 && (
                    <Box textAlign="center" py={12}>
                        <Text fontSize="lg" color="gray.500" fontFamily="Cairo, sans-serif">
                            لا توجد طلبات تسجيل
                        </Text>
                    </Box>
                )}
            </Box>

            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent dir="rtl">
                    <ModalHeader fontFamily="Cairo, sans-serif">تحديث حالة الطلب</ModalHeader>
                    <ModalCloseButton left="12px" right="unset" />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl>
                                <FormLabel fontFamily="Cairo, sans-serif">الحالة</FormLabel>
                                <Select
                                    value={selectedRegistration?.status}
                                    onChange={(e) => setSelectedRegistration({
                                        ...selectedRegistration,
                                        status: e.target.value
                                    })}
                                    fontFamily="Cairo, sans-serif"
                                >
                                    <option value="pending">قيد الانتظار</option>
                                    <option value="contacted">تم التواصل</option>
                                    <option value="enrolled">مسجل</option>
                                    <option value="rejected">مرفوض</option>
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontFamily="Cairo, sans-serif">ملاحظات</FormLabel>
                                <Input
                                    value={selectedRegistration?.notes || ''}
                                    onChange={(e) => setSelectedRegistration({
                                        ...selectedRegistration,
                                        notes: e.target.value
                                    })}
                                    placeholder="أضف ملاحظات..."
                                />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" ml={3} onClick={onClose} fontFamily="Cairo, sans-serif">
                            إلغاء
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={() => handleUpdateStatus(
                                selectedRegistration._id,
                                selectedRegistration.status,
                                selectedRegistration.notes
                            )}
                            fontFamily="Cairo, sans-serif"
                        >
                            حفظ
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Container>
    );
};

export default RegistrationManagement;
